/**
 * Preloads chess piece and board images to ensure they're cached before gameplay.
 * Uses Promise-based loading with decode() for more reliable preloading.
 */
export function preloadChessImages(chessSet: string): Promise<void[]> {
  const pieceTypes = [
    "wP",
    "wN",
    "wB",
    "wR",
    "wQ",
    "wK",
    "bP",
    "bN",
    "bB",
    "bR",
    "bQ",
    "bK",
  ];

  const promises: Promise<void>[] = [];

  // Preload all piece images for the current chess set
  pieceTypes.forEach((piece) => {
    const promise = preloadImage(`/images/pieces/${chessSet}/${piece}.svg`);
    promises.push(promise);
  });

  // Preload board image
  promises.push(preloadImage(`/images/board/${chessSet}.svg`));
  // Also preload the brown board since it's used by multiple sets
  promises.push(preloadImage(`/images/board/brown.svg`));

  return Promise.all(promises);
}

/**
 * Preloads a single image using HTMLImageElement with decode() for reliable caching.
 */
function preloadImage(src: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = src;

    // Use decode() if available for more reliable preloading
    if (img.decode) {
      img.decode().then(() => resolve()).catch(() => resolve());
    } else {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    }
  });
}

/**
 * Returns the list of piece image URLs for a given chess set.
 * Useful for preloading via inline styles in components.
 */
export function getPieceImageUrls(chessSet: string): string[] {
  const pieceTypes = [
    "wP",
    "wN",
    "wB",
    "wR",
    "wQ",
    "wK",
    "bP",
    "bN",
    "bB",
    "bR",
    "bQ",
    "bK",
  ];
  return pieceTypes.map((piece) => `/images/pieces/${chessSet}/${piece}.svg`);
}
